import React, { createContext, useContext, useState, useEffect } from 'react';

interface ProductionContextType {
  isInProduction: boolean;
  productionStartTime: Date | null;
  currentShift: number | null;
}

const ProductionContext = createContext<ProductionContextType>({
  isInProduction: false,
  productionStartTime: null,
  currentShift: null,
});

export const useProduction = () => useContext(ProductionContext);

export const ProductionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInProduction, setIsInProduction] = useState(false);
  const [productionStartTime, setProductionStartTime] = useState<Date | null>(null);
  const [currentShift, setCurrentShift] = useState<number | null>(null);

  const protocol = window.location.protocol;
  const hostname = window.location.hostname || 'localhost';
  const port = window.location.port ? `:${window.location.port}` : '';
  const apiUrl = `${protocol}//${hostname}${port}/api`;

  useEffect(() => {
    // Components should check their specific line mode via /api/line-modes/:lineId
    setIsInProduction(false); // Line-specific production status

    const fetchProductionStatus = async () => {
      try {
        const response = await fetch(`${apiUrl}/production-status`);
        const result = await response.json();
        if (result.message === 'success' && result.data?.currentShift) {
          setCurrentShift(result.data.currentShift.number);
        }
      } catch (error) {
        console.error('Error fetching production status:', error);
      }
    };

    fetchProductionStatus();
    const statusInterval = setInterval(fetchProductionStatus, 5000);

    return () => {
      clearInterval(statusInterval);
    };
  }, [apiUrl]);

  return (
    <ProductionContext.Provider value={{
      isInProduction,
      productionStartTime,
      currentShift,
    }}>
      {children}
    </ProductionContext.Provider>
  );
};